import { createClient } from '@supabase/supabase-js';
import { supabase, getProdSupabaseClient } from '@/lib/supabase/client';

export interface Contact {
  name: string;
  email?: string;
  phone?: string;
  role?: string;
  department?: string;
}

export interface Tenant {
  id: string;
  tenant_name: string; // Column name in prod.tenants
  tenant_slug?: string; // Column name in prod.tenants
  name?: string; // Alias for tenant_name (for backward compatibility)
  slug?: string; // Alias for tenant_slug (for backward compatibility)
  description?: string;
  status?: string; // 'active' | 'inactive' etc.
  active?: boolean; // Derived from status === 'active'
  tenant_type?: string;
  time_zone?: string;
  oem?: string; // Original Equipment Manufacturer (e.g., Ford, Stellantis, etc.) - DEPRECATED: use oems
  oems?: string[]; // Array of OEMs for multi-OEM dealerships (preferred)

  // Contact Information
  primary_email?: string;
  primary_phone?: string;
  website?: string;
  contacts?: Contact[];

  // Address Information
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;

  // Coordinates
  latitude?: number;
  longitude?: number;

  metadata?: Record<string, unknown>;
  zones?: Zone[]; // Array of zone objects associated with this tenant
  zone_ids?: string[]; // Array of zone IDs (for easier management)
  created_at?: string;
  updated_at?: string;
}

export interface Zone {
  id: string;
  zone_name: string;
  zone_slug: string;
  description?: string;
  region?: string;
  country?: string;
  status?: string;
  oem: string; // OEM this zone belongs to (Ford, Hyundai, etc.) - Required
  metadata?: Record<string, unknown>;
  zip_codes?: string[]; // Array of zip codes mapped to this zone
  zip_code_count?: number; // Count of zip codes (for display)
  zone_size?: 'small' | 'medium' | 'large' | 'extra-large'; // Size based on recall count
  total_recalls_at_creation?: number; // Total recalls when zone was created
  unique_vins_at_creation?: number; // Unique VINs when zone was created
  total_recalls_current?: number; // Precomputed current recall count (refreshed periodically)
  latest_snapshot_date?: string; // Crawl date total_recalls_current is "as of"
  created_at?: string;
  updated_at?: string;
  created_by?: string; // User ID who created the zone
  updated_by?: string; // User ID who last updated the zone
  created_by_name?: string; // Display name of creator (from audit logs or user lookup)
  updated_by_name?: string; // Display name of updater (from audit logs or user lookup)
}

export interface ZipCode {
  zip_code: string;
  city?: string;
  state?: string;
  usage_count?: number;
}

export interface ZoneGeometry {
  type: 'Polygon' | 'MultiPolygon';
  coordinates: number[][][] | number[][][][];
}

export interface ZoneMetadata {
  geometry?: ZoneGeometry;
  shape_type?: 'polygon' | 'rectangle' | 'circle' | 'freehand';
  simplified?: boolean;
  [key: string]: unknown;
}

export interface CreateZoneData {
  zone_name: string;
  zone_slug?: string;
  description?: string;
  region?: string;
  country?: string;
  status?: string;
  oem: string; // OEM this zone belongs to (Ford, Hyundai, etc.) - Required
  zip_codes?: string[];
  metadata?: ZoneMetadata;
  zone_size?: 'small' | 'medium' | 'large' | 'extra-large';
  total_recalls_at_creation?: number;
  unique_vins_at_creation?: number;
}

export interface UpdateZoneData {
  zone_name?: string;
  zone_slug?: string;
  description?: string;
  region?: string;
  country?: string;
  status?: string;
  oem?: string; // OEM this zone belongs to (Ford, Hyundai, etc.)
  zip_codes?: string[];
  metadata?: ZoneMetadata;
  total_recalls_at_creation?: number; // Recall count at time of zone creation
}

export interface CreateTenantData {
  tenant_name: string;
  tenant_slug?: string;
  tenant_type?: string;
  status?: string;
  time_zone?: string;
  description?: string;
  oem?: string; // Original Equipment Manufacturer - DEPRECATED: use oems
  oems?: string[]; // Array of OEMs for multi-OEM dealerships (preferred)

  // Contact Information
  primary_email?: string;
  primary_phone?: string;
  website?: string;
  contacts?: Contact[];

  // Address Information
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;

  // Coordinates
  latitude?: number;
  longitude?: number;

  metadata?: Record<string, unknown>;
  zone_ids?: string[]; // Array of zone IDs to associate with tenant
}

export interface UpdateTenantData {
  tenant_name?: string;
  tenant_slug?: string;
  tenant_type?: string;
  status?: string;
  time_zone?: string;
  description?: string;
  oem?: string; // Original Equipment Manufacturer - DEPRECATED: use oems
  oems?: string[]; // Array of OEMs for multi-OEM dealerships (preferred)

  // Contact Information
  primary_email?: string;
  primary_phone?: string;
  website?: string;
  contacts?: Contact[];

  // Address Information
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;

  // Coordinates
  latitude?: number;
  longitude?: number;

  metadata?: Record<string, unknown>;
  zone_ids?: string[]; // Array of zone IDs to associate with tenant
}

// Note: getProdSupabaseClient is imported from '@/lib/supabase/client'
// It provides a Supabase client instance targeted at the 'prod' schema
// with RLS policies enabled (uses anon key, not service role key)

/**
 * ⚠️ SECURITY WARNING: This function uses the service role key in the browser.
 *
 * The service role key should NEVER be exposed to the browser as it bypasses all RLS policies.
 * This is a security risk and should only be used for:
 * - Non-critical operations like audit logging (fire-and-forget)
 * - Server-side operations (migrations, scripts)
 *
 * For regular data operations, use getProdSupabaseClient() instead, which respects RLS policies.
 *
 * @deprecated This function should be removed or moved to server-side only.
 * Use getProdSupabaseClient() for browser-based operations.
 */
const getAdminSupabaseClient = () => {
  const supabaseUrl =
    import.meta.env.VITE_SUPABASE_URL || 'https://exycbmlolhxpacnyqvmw.supabase.co';
  // Vite only exposes variables prefixed with VITE_ to the browser
  // So we need to use VITE_SUPABASE_SERVICE_ROLE_KEY
  const serviceRoleKey =
    import.meta.env['VITE_SUPABASE_SERVICE_ROLE_KEY'] ||
    import.meta.env['SUPABASE_SERVICE_ROLE_KEY'];

  if (!serviceRoleKey) {
    const errorMsg =
      'VITE_SUPABASE_SERVICE_ROLE_KEY environment variable is required for admin operations. ' +
      'Note: In Vite, environment variables must be prefixed with VITE_ to be accessible in the browser. ' +
      'Please add VITE_SUPABASE_SERVICE_ROLE_KEY to your .env.local file and restart the dev server.';
    throw new Error(errorMsg);
  }

  return createClient(supabaseUrl, serviceRoleKey, {
    db: { schema: 'prod' },
    auth: {
      autoRefreshToken: false,
      persistSession: false
    }
  });
};

/**
 * Create admin prod client for audit logging
 * Same as getAdminSupabaseClient but targets prod schema for audit_logs
 */
const getAdminProdClient = () => {
  const supabaseUrl =
    import.meta.env.VITE_SUPABASE_URL || 'https://exycbmlolhxpacnyqvmw.supabase.co';
  const serviceRoleKey =
    import.meta.env['VITE_SUPABASE_SERVICE_ROLE_KEY'] ||
    import.meta.env['SUPABASE_SERVICE_ROLE_KEY'];

  if (!serviceRoleKey) {
    console.warn(
      '⚠️ VITE_SUPABASE_SERVICE_ROLE_KEY not found. Audit logging may be blocked by RLS'
    );
    // Fallback to anon key for non-critical audit logging
    const supabaseAnonKey =
      import.meta.env.VITE_SUPABASE_ANON_KEY ||
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImV4eWNibWxvbGh4cGFjbnlxdm13Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTU3Mjc3OTgsImV4cCI6MjA3MTMwMzc5OH0.dWu9Y8ufHvt8FZLeB6egfFT4-fOnr4i770T1z2c9tq8';

    return createClient(supabaseUrl, supabaseAnonKey, {
      db: { schema: 'prod' },
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true
      }
    });
  }

  return createClient(supabaseUrl, serviceRoleKey, {
    db: { schema: 'prod' },
    auth: {
      autoRefreshToken: false,
      persistSession: false
    }
  });
};

/**
 * Log zone audit events to prod.audit_logs
 * Follows the same pattern as user approval audit logging
 */
const logZoneAudit = async (params: {
  userId: string;
  action: string;
  resourceId: string;
  oldValues?: Record<string, any>;
  newValues?: Record<string, any>;
  tenantId?: string;
}) => {
  try {
    const adminClient = getAdminProdClient();
    await adminClient.from('audit_logs').insert({
      tenant_id: params.tenantId || null,
      user_id: params.userId,
      action: params.action,
      resource_type: 'zone',
      resource_id: params.resourceId,
      old_values: params.oldValues || null,
      new_values: params.newValues || null,
      created_at: new Date().toISOString()
    });
  } catch (auditError) {
    // Silently handle audit logging errors to not break the main operation
    console.warn('Zone audit logging failed:', auditError);
  }
};

/**
 * Service for managing tenant data
 *
 * Uses a separate Supabase client instance targeting the 'prod' schema
 * to query prod.tenants directly. RLS policies ensure users only see
 * tenants they belong to (via user_tenants table).
 */
export const tenantAPI = {
  /**
   * Diagnostic function to check user_tenants entries
   * This helps debug RLS issues
   */
  async debugUserTenants(): Promise<void> {
    // Diagnostic function - no-op in production
  },

  /**
   * Fetch all tenants (for admins)
   * Uses SECURITY DEFINER RPC function via public schema wrapper.
   * The RPC function checks for super_admin role internally.
   *
   * Best Practice: RPC functions with SECURITY DEFINER bypass RLS and run with
   * the privileges of the function owner. No service role key needed in browser.
   *
   * Note: Uses public schema wrapper function that delegates to prod schema function.
   * This is necessary because PostgREST searches public schema by default for RPC calls.
   */
  async getAllTenants(): Promise<Tenant[]> {
    try {
      console.log('[getAllTenants] Starting RPC call to get_all_tenants_for_admin...');

      // Check session first
      const {
        data: { session },
        error: sessionError
      } = await supabase.auth.getSession();
      if (sessionError) {
        console.error('[getAllTenants] Session error:', sessionError);
        throw sessionError;
      }
      if (!session) {
        console.warn('[getAllTenants] No session available, cannot call RPC');
        return [];
      }
      console.log('[getAllTenants] Session found, user:', session.user?.email);

      // Use main supabase client - public schema wrapper delegates to prod function
      const { data, error } = await supabase.rpc('get_all_tenants_for_admin');

      if (error) {
        console.error('[getAllTenants] RPC error:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }

      console.log('[getAllTenants] RPC success, received', data?.length || 0, 'tenants');

      // Fetch zones for each tenant and transform
      const tenantsWithZones = await Promise.all(
        (data || []).map(async (tenant: any) => {
          const zones = await this.getTenantZones(tenant.id);

          // Parse contacts if it's a JSONB array
          let contacts: Contact[] = [];
          if (tenant.contacts) {
            try {
              contacts = Array.isArray(tenant.contacts)
                ? tenant.contacts
                : JSON.parse(tenant.contacts);
            } catch {
              contacts = [];
            }
          }

          return {
            ...tenant,
            name: tenant.tenant_name,
            slug: tenant.tenant_slug,
            active: tenant.status === 'active',
            zones,
            zone_ids: zones.map((z: Zone) => z.id),
            contacts
          };
        })
      );

      console.log(
        '[getAllTenants] Completed, returning',
        tenantsWithZones.length,
        'tenants with zones'
      );
      return tenantsWithZones;
    } catch (error) {
      console.error('[getAllTenants] Error:', error);
      // Re-throw the error so React Query can handle it
      throw error;
    }
  },

  /**
   * Fetch tenants for the current user
   * Uses a Supabase client targeting the 'prod' schema to query prod.tenants directly.
   * RLS policies ensure users only see tenants they belong to (via user_tenants table).
   */
  async getTenants(): Promise<Tenant[]> {
    try {
      // First, verify we have a session
      const {
        data: { session: mainSession },
        error: sessionError
      } = await supabase.auth.getSession();
      if (sessionError || !mainSession) {
        return [];
      }

      // Get the prod schema client (shares auth session via localStorage)
      const sbProd = getProdSupabaseClient();

      // Verify the prod client has the session
      // Both clients use the same storageKey, so they automatically share the session
      // No need to call setSession() which internally calls /auth/v1/user and fails in cross-site scenarios
      // Verify session is available
      const {
        data: { session: prodSession },
        error: prodSessionError
      } = await sbProd.auth.getSession();
      if (prodSessionError || !prodSession) {
        // If no session in prod client, verify it doesn't exist in main client either
        if (!mainSession) {
          throw new Error('No authentication session available');
        }
        // Session exists in main client but not in prod client
        // This shouldn't happen if storageKey is the same, but handle gracefully
        throw new Error('Session exists in main client but not accessible in prod client');
      }

      // Query prod.tenants directly - RLS will filter based on user_tenants
      // Note: Columns are tenant_name and tenant_slug, not name and slug
      const { data, error } = await sbProd
        .from('tenants')
        .select(
          'id,tenant_name,tenant_slug,status,tenant_type,time_zone,oem,metadata,created_at,updated_at'
        )
        .order('tenant_name', { ascending: true });

      if (error) {
        throw error;
      }

      // Transform data to include both original column names and aliases for compatibility
      return (data || []).map((tenant: any) => ({
        ...tenant,
        name: tenant.tenant_name, // Alias for backward compatibility
        slug: tenant.tenant_slug, // Alias for backward compatibility
        active: tenant.status === 'active' // Derive active from status
      }));
    } catch (error) {
      // Return empty array on error to prevent UI breakage
      return [];
    }
  },

  /**
   * Get a single tenant by ID
   * Uses prod schema client to query prod.tenants directly
   */
  async getTenantById(tenantId: string): Promise<Tenant | null> {
    try {
      const sbProd = getProdSupabaseClient();

      const { data, error } = await sbProd
        .from('tenants')
        .select(
          'id,tenant_name,tenant_slug,status,tenant_type,time_zone,oem,latitude,longitude,metadata,created_at,updated_at'
        )
        .eq('id', tenantId)
        .maybeSingle();

      if (error) {
        return null;
      }

      if (!data) return null;

      // Transform to include aliases
      return {
        ...data,
        name: data.tenant_name,
        slug: data.tenant_slug,
        active: data.status === 'active'
      };
    } catch (error) {
      return null;
    }
  },

  /**
   * Get a tenant by slug
   * Uses prod schema client to query prod.tenants directly
   */
  async getTenantBySlug(slug: string): Promise<Tenant | null> {
    try {
      const sbProd = getProdSupabaseClient();

      const { data, error } = await sbProd
        .from('tenants')
        .select(
          'id,tenant_name,tenant_slug,status,tenant_type,time_zone,oem,metadata,created_at,updated_at'
        )
        .eq('tenant_slug', slug)
        .maybeSingle();

      if (error) {
        return null;
      }

      if (!data) return null;

      // Transform to include aliases
      return {
        ...data,
        name: data.tenant_name,
        slug: data.tenant_slug,
        active: data.status === 'active'
      };
    } catch (error) {
      return null;
    }
  },

  /**
   * Get tenants for a specific user
   * Uses SECURITY DEFINER RPC function to bypass RLS on user_tenants table
   * Requires super_admin role (verified inside the RPC function)
   * @param userId The user ID to get tenants for
   * @param _useAdminClient Deprecated parameter, kept for backward compatibility
   */
  async getUserTenants(userId: string, _useAdminClient: boolean = false): Promise<Tenant[]> {
    try {
      console.log(`[getUserTenants] Fetching tenants for user ${userId} via RPC`);

      // Use the SECURITY DEFINER RPC function - bypasses RLS
      const { data: tenants, error } = await supabase.rpc('get_user_tenants_for_admin', {
        p_user_id: userId
      });

      if (error) {
        // If RPC doesn't exist yet, fall back to direct query
        if (error.message?.includes('function') && error.message?.includes('does not exist')) {
          console.warn('[getUserTenants] RPC not available, falling back to direct query');
          return this.getUserTenantsFallback(userId);
        }

        console.error('[getUserTenants] RPC error:', error);
        return [];
      }

      if (!tenants || tenants.length === 0) {
        console.log('[getUserTenants] No tenants found for user', userId);
        return [];
      }

      console.log(
        '[getUserTenants] Found',
        tenants.length,
        'tenant associations for user',
        userId,
        ':',
        tenants.map((t: any) => t.tenant_id)
      );

      console.log(
        '[getUserTenants] Tenant names:',
        tenants.map((t: any) => t.tenant_name)
      );

      // Map RPC result to Tenant type
      return (tenants || []).map((tenant: any) => ({
        id: tenant.tenant_id,
        tenant_name: tenant.tenant_name,
        tenant_slug: tenant.tenant_slug,
        status: tenant.status,
        tenant_type: tenant.tenant_type,
        time_zone: tenant.time_zone,
        oem: tenant.oem,
        created_at: tenant.created_at,
        updated_at: tenant.updated_at,
        // Also include legacy field names for compatibility
        name: tenant.tenant_name,
        slug: tenant.tenant_slug,
        active: tenant.status === 'active'
      }));
    } catch (error) {
      console.error('[getUserTenants] Unexpected error:', error);
      return [];
    }
  },

  /**
   * Get tenants for the CURRENT logged-in user (non-admin)
   * Uses get_my_tenants RPC which only returns the caller's own tenants
   * Does NOT require super_admin role - safe for all authenticated users
   */
  async getMyTenants(): Promise<Tenant[]> {
    try {
      console.log('[getMyTenants] Fetching tenants for current user via RPC');

      // Use the non-admin SECURITY DEFINER RPC function
      const { data: tenants, error } = await supabase.rpc('get_my_tenants');

      if (error) {
        // If RPC doesn't exist yet, fall back to getTenants (direct query with RLS)
        if (error.message?.includes('function') && error.message?.includes('does not exist')) {
          console.warn('[getMyTenants] RPC not available, falling back to getTenants');
          return this.getTenants();
        }

        console.error('[getMyTenants] RPC error:', error);
        return [];
      }

      if (!tenants || tenants.length === 0) {
        console.log('[getMyTenants] No tenants found for current user');
        return [];
      }

      console.log(
        '[getMyTenants] Found',
        tenants.length,
        'tenant(s):',
        tenants.map((t: any) => t.tenant_name)
      );

      // Map RPC result to Tenant type
      return (tenants || []).map((tenant: any) => ({
        id: tenant.tenant_id,
        tenant_name: tenant.tenant_name,
        tenant_slug: tenant.tenant_slug,
        status: tenant.status,
        tenant_type: tenant.tenant_type,
        time_zone: tenant.time_zone,
        oem: tenant.oem,
        created_at: tenant.created_at,
        updated_at: tenant.updated_at,
        // Also include legacy field names for compatibility
        name: tenant.tenant_name,
        slug: tenant.tenant_slug,
        active: tenant.is_active
      }));
    } catch (error) {
      console.error('[getMyTenants] Unexpected error:', error);
      return [];
    }
  },

  /**
   * Fallback method for getUserTenants when RPC is not available
   * Uses direct table queries (may be blocked by RLS)
   */
  async getUserTenantsFallback(userId: string): Promise<Tenant[]> {
    try {
      // Use prod client - RLS policies may allow super_admin to read all records
      const prodClient = getProdSupabaseClient();
      const { data: userTenants, error: utError } = await prodClient
        .from('user_tenants')
        .select('tenant_id')
        .eq('user_id', userId);

      if (utError) {
        console.error(
          '[getUserTenantsFallback] Error fetching user_tenants for user',
          userId,
          ':',
          utError
        );
        // Return empty array to maintain backward compatibility
        return [];
      }

      if (!userTenants || userTenants.length === 0) {
        console.log('[getUserTenantsFallback] No tenants found for user', userId);
        return [];
      }

      console.log(
        '[getUserTenantsFallback] Found',
        userTenants.length,
        'tenant associations for user',
        userId,
        ':',
        userTenants.map(ut => ut.tenant_id)
      );

      // Then fetch tenant details from prod.tenants
      const tenantIds = userTenants.map(ut => ut.tenant_id);

      console.log('[getUserTenantsFallback] Querying prod.tenants for IDs:', tenantIds);

      const { data: tenants, error: tenantsError } = await prodClient
        .from('tenants')
        .select(
          'id,tenant_name,tenant_slug,status,tenant_type,time_zone,oem,metadata,created_at,updated_at'
        )
        .in('id', tenantIds)
        .order('tenant_name', { ascending: true });

      if (tenantsError) {
        console.error('[getUserTenantsFallback] Error fetching tenant details:', tenantsError);
        // Return empty array to maintain backward compatibility
        return [];
      }

      console.log(
        '[getUserTenantsFallback] Successfully fetched',
        tenants?.length || 0,
        'tenant details'
      );
      if (tenants && tenants.length > 0) {
        console.log(
          '[getUserTenantsFallback] Tenant names:',
          tenants.map(t => t.tenant_name)
        );
      } else if (tenantIds.length > 0) {
        console.warn(
          '[getUserTenantsFallback] WARNING: Found',
          tenantIds.length,
          'tenant IDs in user_tenants but 0 tenants in prod.tenants. This suggests:'
        );
        console.warn(
          "  - Tenant IDs don't match between user_tenants.tenant_id and prod.tenants.id"
        );
        console.warn('  - Or RLS policies are blocking access to prod.tenants');
        console.warn('  - Or tenants have been deleted from prod.tenants');
      }
      return (tenants || []).map((tenant: any) => ({
        ...tenant,
        name: tenant.tenant_name,
        slug: tenant.tenant_slug,
        active: tenant.status === 'active'
      }));
    } catch (error) {
      console.error('[getUserTenantsFallback] Unexpected error:', error);
      // Return empty array to maintain backward compatibility
      return [];
    }
  },

  /**
   * Add tenant to user
   * Uses SECURITY DEFINER RPC function to bypass RLS on user_tenants table
   * Requires super_admin role (verified inside the RPC function)
   */
  async addUserTenant(userId: string, tenantId: string): Promise<void> {
    try {
      console.log(`[addUserTenant] Adding tenant ${tenantId} for user ${userId} via RPC`);

      // Use the SECURITY DEFINER RPC function - bypasses RLS
      // The function checks for super_admin role internally
      const { data, error } = await supabase.rpc('add_user_tenant_for_admin', {
        p_user_id: userId,
        p_tenant_id: tenantId
      });

      if (error) {
        console.error('[addUserTenant] RPC error:', error);

        // Handle specific error cases
        if (error.message?.includes('Access denied')) {
          throw new Error('Access denied. Super admin role required to add tenant associations.');
        }
        if (error.message?.includes('does not exist')) {
          throw new Error(error.message);
        }

        throw error;
      }

      const result = data as { success: boolean; message: string; already_existed: boolean };
      console.log('[addUserTenant] RPC success:', result);

      if (result.already_existed) {
        console.log('[addUserTenant] Tenant association already existed');
      } else {
        console.log(`[addUserTenant] Successfully added tenant ${tenantId} for user ${userId}`);
      }
    } catch (error) {
      console.error('[addUserTenant] Unexpected error:', error);
      throw error;
    }
  },

  /**
   * Remove tenant from user
   * Uses SECURITY DEFINER RPC function to bypass RLS on user_tenants table
   * Requires super_admin role (verified inside the RPC function)
   */
  async removeUserTenant(userId: string, tenantId: string): Promise<void> {
    try {
      console.log(`[removeUserTenant] Removing tenant ${tenantId} from user ${userId} via RPC`);

      // Use the SECURITY DEFINER RPC function - bypasses RLS
      // The function checks for super_admin role internally
      const { data, error } = await supabase.rpc('remove_user_tenant_for_admin', {
        p_user_id: userId,
        p_tenant_id: tenantId
      });

      if (error) {
        console.error('[removeUserTenant] RPC error:', error);

        // Handle specific error cases
        if (error.message?.includes('Access denied')) {
          throw new Error(
            'Access denied. Super admin role required to remove tenant associations.'
          );
        }

        throw error;
      }

      const result = data as { success: boolean; message: string; deleted_count: number };
      console.log('[removeUserTenant] RPC success:', result);

      if (result.deleted_count > 0) {
        console.log(
          `[removeUserTenant] Successfully removed tenant ${tenantId} from user ${userId}`
        );
      } else {
        console.log('[removeUserTenant] Tenant association did not exist (no rows deleted)');
      }
    } catch (error) {
      console.error('[removeUserTenant] Unexpected error:', error);
      throw error;
    }
  },

  /**
   * Get all available zones from prod.zones table
   * Uses SECURITY DEFINER RPC function - works with regular authenticated client
   *
   * Best Practice: RPC functions with SECURITY DEFINER bypass RLS and run with
   * the privileges of the function owner. This is the recommended Supabase pattern
   * for admin operations - no service role key needed in the browser.
   */
  async getAvailableZones(): Promise<Zone[]> {
    try {
      // First try the RPC function (recommended approach)
      const zones = await this.getAvailableZonesViaRPC();
      if (zones.length > 0) {
        return zones;
      }

      // Fallback to direct query (may be blocked by RLS)
      const prodClient = getProdSupabaseClient();
      const { data, error } = await prodClient
        .from('zones')
        .select(
          'id,zone_name,zone_slug,description,region,country,status,metadata,created_at,updated_at'
        )
        .eq('status', 'active')
        .order('zone_name', { ascending: true });

      if (error) {
        throw error;
      }

      return (data || []).map((zone: any) => ({
        id: zone.id,
        zone_name: zone.zone_name,
        zone_slug: zone.zone_slug,
        description: zone.description,
        region: zone.region,
        country: zone.country,
        status: zone.status,
        metadata: zone.metadata,
        created_at: zone.created_at,
        updated_at: zone.updated_at
      }));
    } catch (error) {
      console.error('Error fetching zones:', error);
      // Fallback: try to get zones from tasks if zones table doesn't exist yet
      return this.getAvailableZonesFallback();
    }
  },

  /**
   * Try to get zones via RPC function (if available)
   * RPC functions run with SECURITY DEFINER and may bypass RLS
   */
  async getAvailableZonesViaRPC(): Promise<Zone[]> {
    try {
      const prodClient = getProdSupabaseClient();

      // Try using the RPC function that gets zones with counts
      const { data, error } = await prodClient.rpc('get_all_zones_with_counts');

      if (error) {
        // RPC function might not exist or might also be blocked
        console.warn('RPC function get_all_zones_with_counts failed:', error);
        return [];
      }

      // Transform RPC response to Zone format
      return (data || [])
        .filter((zone: any) => zone.status === 'active')
        .map((zone: any) => ({
          id: zone.id,
          zone_name: zone.zone_name,
          zone_slug: zone.zone_slug,
          description: zone.description,
          region: zone.region,
          country: zone.country,
          status: zone.status,
          metadata: zone.metadata,
          created_at: zone.created_at,
          updated_at: zone.updated_at
        }))
        .sort((a: Zone, b: Zone) => a.zone_name.localeCompare(b.zone_name));
    } catch (error) {
      console.warn('Error in RPC zone fetch:', error);
      return [];
    }
  },

  /**
   * Fallback method to get zones from data_collection_actor_tasks
   * Used if zones table doesn't exist yet or RPC function is not available
   */
  async getAvailableZonesFallback(): Promise<Zone[]> {
    try {
      // Use regular prod client - this may be blocked by RLS
      const prodClient = getProdSupabaseClient();

      const { data, error } = await prodClient
        .from('data_collection_actor_tasks')
        .select('zone,zone_slug')
        .not('zone', 'is', null)
        .not('zone_slug', 'is', null);

      if (error) {
        console.warn('Fallback zone fetch blocked (likely RLS):', error.message);
        return [];
      }

      // Get unique zones and create Zone objects with temporary IDs
      const zoneMap = new Map<string, Zone>();
      (data || []).forEach((task: any) => {
        if (task.zone && task.zone_slug) {
          const key = `${task.zone_slug}`;
          if (!zoneMap.has(key)) {
            zoneMap.set(key, {
              id: `temp-${key}`, // Temporary ID
              zone_name: task.zone,
              zone_slug: task.zone_slug
            });
          }
        }
      });

      return Array.from(zoneMap.values()).sort((a, b) => a.zone_name.localeCompare(b.zone_name));
    } catch (error) {
      console.error('Error in fallback zone fetch:', error);
      return [];
    }
  },

  /**
   * Get zones associated with a tenant from tenant_zones_view
   * Uses SECURITY DEFINER RPC function for super_admin access
   */
  async getTenantZones(tenantId: string): Promise<Zone[]> {
    // Helper function to map RPC data to Zone interface
    const mapViewDataToZones = (viewData: any[]): Zone[] => {
      if (!viewData || viewData.length === 0) {
        return [];
      }

      return viewData
        .map((tz: any) => {
          // Map view columns to Zone interface
          return {
            id: tz.zone_id || tz.id,
            zone_name: tz.zone_name,
            zone_slug: tz.zone_slug,
            description: tz.description,
            region: tz.region,
            country: tz.country,
            status: tz.status,
            metadata: tz.metadata,
            created_at: tz.created_at,
            updated_at: tz.updated_at
          };
        })
        .filter((zone: any) => zone.id) as Zone[];
    };

    try {
      // Use SECURITY DEFINER RPC function to bypass RLS on the view
      const { data: tenantZonesData, error: tenantZonesError } = await supabase.rpc(
        'get_tenant_zones_for_admin',
        { p_tenant_id: tenantId }
      );

      if (tenantZonesError) {
        console.error('Error fetching tenant zones via RPC:', {
          error: tenantZonesError,
          message: tenantZonesError.message,
          code: tenantZonesError.code,
          details: tenantZonesError.details,
          hint: tenantZonesError.hint,
          tenantId
        });
        // RPC might fail, return empty array
        return [];
      }

      return mapViewDataToZones(tenantZonesData || []);
    } catch (error) {
      console.error('Error fetching tenant zones:', error);
      return [];
    }
  },

  /**
   * Create a new tenant
   * Uses SECURITY DEFINER RPC function - works with regular authenticated client
   */
  async createTenant(data: CreateTenantData): Promise<Tenant> {
    try {
      // Use main client - public schema wrapper delegates to prod function
      // (prodClient.rpc() doesn't reliably resolve functions in the prod schema)

      // Generate tenant_slug if not provided
      const tenantSlug =
        data.tenant_slug || data.tenant_name.toLowerCase().replace(/[^a-z0-9]+/g, '-');

      // Call the RPC function via public schema wrapper
      const { data: result, error } = await supabase.rpc('create_tenant_for_admin', {
        p_tenant_name: data.tenant_name,
        p_tenant_slug: tenantSlug,
        p_tenant_type: data.tenant_type || 'dealer',
        p_status: data.status || 'active',
        p_time_zone: data.time_zone || 'America/New_York',
        p_description: data.description || null,
        p_primary_email: data.primary_email || null,
        p_primary_phone: data.primary_phone || null,
        p_website: data.website || null,
        p_address_line1: data.address_line1 || null,
        p_address_line2: data.address_line2 || null,
        p_city: data.city || null,
        p_state: data.state || null,
        p_region: data.state || null, // Use state as region for now
        p_postal_code: data.postal_code || null,
        p_country: data.country || 'US',
        p_oem: data.oem || null, // Legacy single OEM (deprecated)
        p_latitude: data.latitude ?? null,
        p_longitude: data.longitude ?? null,
        p_contacts: data.contacts && data.contacts.length > 0 ? data.contacts : [],
        p_metadata: data.metadata || {},
        p_oems: data.oems && data.oems.length > 0 ? data.oems : null // Multi-OEM array (preferred)
      });

      if (error) {
        throw error;
      }

      // RPC returns an array, get the first item
      const newTenant = Array.isArray(result) ? result[0] : result;

      if (!newTenant) {
        throw new Error('Failed to create tenant - no data returned');
      }

      // Create zone associations if zone_ids provided
      if (data.zone_ids && data.zone_ids.length > 0) {
        await this.setTenantZones(newTenant.id, data.zone_ids);
      }

      // Fetch zones for the new tenant
      const zones = await this.getTenantZones(newTenant.id);

      // Parse contacts
      let contacts: Contact[] = [];
      if (newTenant.contacts) {
        try {
          contacts = Array.isArray(newTenant.contacts)
            ? newTenant.contacts
            : JSON.parse(newTenant.contacts);
        } catch {
          contacts = [];
        }
      }

      return {
        ...newTenant,
        name: newTenant.tenant_name,
        slug: newTenant.tenant_slug,
        active: newTenant.status === 'active',
        zones,
        zone_ids: zones.map(z => z.id),
        contacts
      };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update an existing tenant
   * Uses SECURITY DEFINER RPC function - works with regular authenticated client
   */
  async updateTenant(tenantId: string, data: UpdateTenantData): Promise<Tenant> {
    try {
      // Use main client - public schema wrapper delegates to prod function
      // (prodClient.rpc() doesn't reliably resolve functions in the prod schema)

      // Call the RPC function via public schema wrapper
      const { data: result, error } = await supabase.rpc('update_tenant_for_admin', {
        p_tenant_id: tenantId,
        p_tenant_name: data.tenant_name ?? null,
        p_tenant_slug: data.tenant_slug ?? null,
        p_tenant_type: data.tenant_type ?? null,
        p_status: data.status ?? null,
        p_time_zone: data.time_zone ?? null,
        p_description: data.description ?? null,
        p_primary_email: data.primary_email ?? null,
        p_primary_phone: data.primary_phone ?? null,
        p_website: data.website ?? null,
        p_address_line1: data.address_line1 ?? null,
        p_address_line2: data.address_line2 ?? null,
        p_city: data.city ?? null,
        p_state: data.state ?? null,
        p_region: data.state ?? null, // Use state as region
        p_postal_code: data.postal_code ?? null,
        p_country: data.country ?? null,
        p_oem: data.oem ?? null, // Legacy single OEM (deprecated)
        p_latitude: data.latitude ?? null,
        p_longitude: data.longitude ?? null,
        p_contacts: data.contacts ?? null,
        p_metadata: data.metadata ?? null,
        p_oems: data.oems ?? null // Multi-OEM array (preferred)
      });

      if (error) {
        throw error;
      }

      // RPC returns an array, get the first item
      const updatedTenant = Array.isArray(result) ? result[0] : result;

      if (!updatedTenant) {
        throw new Error('Failed to update tenant - no data returned');
      }

      // Update zone associations if zone_ids provided
      if (data.zone_ids !== undefined) {
        await this.setTenantZones(tenantId, data.zone_ids);
      }

      // Get zones for the tenant
      const zones = await this.getTenantZones(tenantId);

      // Parse contacts
      let contacts: Contact[] = [];
      if (updatedTenant.contacts) {
        try {
          contacts = Array.isArray(updatedTenant.contacts)
            ? updatedTenant.contacts
            : JSON.parse(updatedTenant.contacts);
        } catch {
          contacts = [];
        }
      }

      return {
        ...updatedTenant,
        name: updatedTenant.tenant_name,
        slug: updatedTenant.tenant_slug,
        active: updatedTenant.status === 'active',
        zones,
        zone_ids: zones.map(z => z.id),
        contacts
      };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Set zones for a tenant using zone IDs (creates/updates tenant_zones associations)
   * Uses SECURITY DEFINER RPC function to bypass RLS on tenant_zones table
   */
  async setTenantZones(tenantId: string, zoneIds: string[]): Promise<void> {
    try {
      // Filter out temporary IDs and empty strings
      const validZoneIds = zoneIds
        .filter(id => id && !id.startsWith('temp-'))
        .map(id => id.trim())
        .filter(id => id.length > 0);

      // Use main client - public schema wrapper delegates to prod function
      const { data, error } = await supabase.rpc('set_tenant_zones_for_admin', {
        p_tenant_id: tenantId,
        p_zone_ids: validZoneIds
      });

      if (error) {
        throw new Error(`Failed to set tenant zones: ${error.message}`);
      }
    } catch (error) {
      console.error('Error setting tenant zones:', error);
      throw error;
    }
  },

  /**
   * Delete a tenant (soft delete by setting status to 'inactive' or hard delete)
   * Uses SECURITY DEFINER RPC function - works with regular authenticated client
   */
  async deleteTenant(tenantId: string, hardDelete: boolean = false): Promise<void> {
    try {
      if (hardDelete) {
        // Hard delete using RPC function via public schema wrapper
        const { error } = await supabase.rpc('delete_tenant_for_admin', {
          p_tenant_id: tenantId
        });

        if (error) {
          throw error;
        }
      } else {
        // Soft delete - set status to inactive using update RPC
        await this.updateTenant(tenantId, { status: 'inactive' });
      }
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get all zones with their zip code counts (optimized version)
   * Uses SECURITY DEFINER RPC function - works with regular authenticated client
   *
   * Best Practice: RPC functions with SECURITY DEFINER bypass RLS and run with
   * the privileges of the function owner. This is the recommended Supabase pattern
   * for admin operations - no service role key needed in the browser.
   */
  async getAllZonesWithZipCounts(): Promise<Zone[]> {
    try {
      console.log('[getAllZonesWithZipCounts] Starting RPC call...');

      // Use main supabase client - public schema wrapper delegates to prod function
      const { data, error } = await supabase.rpc('get_all_zones_with_counts');

      if (error) {
        console.error('[getAllZonesWithZipCounts] RPC error:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }

      console.log('[getAllZonesWithZipCounts] RPC success, received', data?.length || 0, 'zones');

      // Transform the result to match the Zone interface
      return (data || []).map((zone: any) => ({
        ...zone,
        zip_codes: [], // We don't fetch actual zip codes in list view for performance
        zip_code_count: Number(zone.zip_code_count) || 0,
        zone_size: zone.zone_size || 'small',
        total_recalls_at_creation: zone.total_recalls_at_creation || 0,
        unique_vins_at_creation: zone.unique_vins_at_creation || 0,
        // Mapped explicitly (not just via the spread) because the zone map's size label,
        // colour and dash pattern are all derived from it.
        total_recalls_current: Number(zone.total_recalls_current) || 0,
        latest_snapshot_date: zone.latest_snapshot_date || undefined
      }));
    } catch (error) {
      console.error('[getAllZonesWithZipCounts] Error:', error);
      // Fallback to direct query (may be blocked by RLS if no SECURITY DEFINER function)
      try {
        const prodClient = getProdSupabaseClient();
        const { data: zones, error: fallbackError } = await prodClient
          .from('zones')
          .select('*')
          .order('zone_name', { ascending: true });

        if (fallbackError) throw fallbackError;

        // For fallback, just return zones without counts to avoid N+1 queries
        return (zones || []).map((zone: any) => ({
          ...zone,
          zip_codes: [],
          zip_code_count: 0,
          zone_size: zone.zone_size || 'small',
          total_recalls_at_creation: zone.total_recalls_at_creation || 0,
          unique_vins_at_creation: zone.unique_vins_at_creation || 0,
          total_recalls_current: Number(zone.total_recalls_current) || 0
        }));
      } catch (fallbackError) {
        throw error; // Throw original error
      }
    }
  },

  /**
   * Get unique regions from all zones
   * Uses SECURITY DEFINER RPC function to get zones, then extracts unique regions.
   */
  async getUniqueRegions(): Promise<string[]> {
    try {
      // Use the RPC function that bypasses RLS
      const prodClient = getProdSupabaseClient();
      const { data, error } = await prodClient.rpc('get_all_zones_with_counts');

      if (error) {
        console.warn('Error fetching zones for regions:', error);
        return [];
      }

      // Extract unique regions from the zones data
      const regions = [...new Set((data || []).map((z: any) => z.region).filter(Boolean))];
      return (regions as string[]).sort();
    } catch (error) {
      console.warn('Error getting unique regions:', error);
      return [];
    }
  },

  /**
   * Get zones with pagination (optimized for large datasets)
   * Uses SECURITY DEFINER RPC function - works with regular authenticated client
   *
   * Best Practice: RPC functions with SECURITY DEFINER bypass RLS and run with
   * the privileges of the function owner. This is the recommended Supabase pattern
   * for admin operations - no service role key needed in the browser.
   *
   * @param includeUserNames - If false, skips expensive user name lookup for faster initial load
   */
  async getZonesPaginated(
    page: number = 1,
    limit: number = 50,
    searchTerm?: string,
    statusFilter?: 'all' | 'active' | 'inactive',
    regionFilter?: string,
    oemFilter?: string | string[], // Filter by OEM(s) - single string or array (null = all OEMs)
    includeUserNames: boolean = false // Default to false for performance
  ): Promise<{ zones: Zone[]; totalCount: number; hasMore: boolean }> {
    try {
      // Use regular prod client - RPC function has SECURITY DEFINER
      const prodClient = getProdSupabaseClient();
      const offset = (page - 1) * limit;

      // Normalize OEM filter to support both single and array formats
      const singleOemFilter = typeof oemFilter === 'string' ? oemFilter : null;
      const arrayOemFilter = Array.isArray(oemFilter) && oemFilter.length > 0 ? oemFilter : null;

      // Use the optimized RPC function for paginated zones
      const { data, error } = await prodClient.rpc('get_zones_paginated', {
        p_limit: limit,
        p_offset: offset,
        p_search_term: searchTerm || null,
        p_status_filter: statusFilter || 'all',
        p_region_filter: regionFilter || null,
        p_oem_filter: singleOemFilter, // Legacy single OEM filter
        p_oem_filters: arrayOemFilter // Multi-OEM array filter (preferred)
      });

      if (error) {
        // Fallback to direct query if RPC doesn't exist
        console.warn('Paginated zones RPC not available, using direct query:', error);

        // Build query with filters (use same prodClient - may be blocked by RLS)
        let query = prodClient.from('zones').select('*, zone_zip_codes(count)', { count: 'exact' });

        // Apply search filter
        if (searchTerm && searchTerm.trim()) {
          query = query.or(
            `zone_name.ilike.%${searchTerm}%,zone_slug.ilike.%${searchTerm}%,region.ilike.%${searchTerm}%`
          );
        }

        // Apply status filter
        if (statusFilter && statusFilter !== 'all') {
          if (statusFilter === 'active') {
            query = query.eq('status', 'active');
          } else {
            query = query.neq('status', 'active');
          }
        }

        // Apply region filter
        if (regionFilter && regionFilter !== 'all') {
          query = query.eq('region', regionFilter);
        }

        // Apply OEM filter (supports both single string and array)
        if (Array.isArray(oemFilter) && oemFilter.length > 0) {
          query = query.in('oem', oemFilter);
        } else if (typeof oemFilter === 'string' && oemFilter) {
          query = query.eq('oem', oemFilter);
        }

        // Apply pagination and ordering
        query = query.order('zone_name', { ascending: true }).range(offset, offset + limit - 1);

        const { data: fallbackData, error: fallbackError, count } = await query;

        if (fallbackError) {
          throw fallbackError;
        }

        // Transform the result to include zip code counts
        let zones = (fallbackData || []).map((zone: any) => ({
          ...zone,
          zip_codes: [], // Don't fetch actual zip codes for performance
          zip_code_count: zone.zone_zip_codes?.[0]?.count || 0,
          zone_size: zone.zone_size || 'small',
          total_recalls_at_creation: zone.total_recalls_at_creation || 0,
          unique_vins_at_creation: zone.unique_vins_at_creation || 0,
          created_by: zone.created_by,
          updated_by: zone.updated_by
        }));

        // Fetch user names for created_by and updated_by from audit logs (only if requested)
        if (includeUserNames && zones.length > 0) {
          const zoneIds = zones.map(z => z.id);
          const { data: auditLogs, error: auditError } = await prodClient
            .from('audit_logs')
            .select('resource_id, user_id, action, created_at')
            .eq('resource_type', 'zone')
            .in('resource_id', zoneIds)
            .in('action', ['zone.created', 'zone.updated'])
            .order('created_at', { ascending: false });

          if (auditError) {
            console.warn('Error fetching audit logs for zones (fallback):', auditError);
          }

          // Get unique user IDs from audit logs
          const userIds = new Set<string>();
          auditLogs?.forEach(log => {
            if (log.user_id) userIds.add(log.user_id);
          });

          // Fetch user names from multiple sources
          const userNamesMap = new Map<string, string>();
          if (userIds.size > 0) {
            const userIdArray = Array.from(userIds);

            // Try to get from user_profiles first (prod schema)
            try {
              const { data: userProfiles, error: profileError } = await prodClient
                .schema('prod')
                .from('user_profiles')
                .select('user_id, first_name, last_name')
                .in('user_id', userIdArray);

              if (!profileError && userProfiles) {
                userProfiles.forEach((user: any) => {
                  const displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
                  if (displayName) {
                    userNamesMap.set(user.user_id, displayName);
                  }
                });
              }
            } catch (profileErr) {
              console.warn('Error fetching user profiles (fallback):', profileErr);
            }

            // Fallback: Try to get from auth.users using admin auth client
            const missingUserIds = userIdArray.filter(id => !userNamesMap.has(id));
            if (missingUserIds.length > 0) {
              try {
                // Create admin auth client for accessing auth.users
                const supabaseUrl =
                  import.meta.env.VITE_SUPABASE_URL || 'https://exycbmlolhxpacnyqvmw.supabase.co';
                const serviceRoleKey =
                  import.meta.env['VITE_SUPABASE_SERVICE_ROLE_KEY'] ||
                  import.meta.env['SUPABASE_SERVICE_ROLE_KEY'];

                if (serviceRoleKey) {
                  const adminAuthClient = createClient(supabaseUrl, serviceRoleKey, {
                    auth: {
                      autoRefreshToken: false,
                      persistSession: false
                    }
                  });

                  // Fetch user info from auth.users for missing users
                  for (const userId of missingUserIds) {
                    try {
                      const { data: authUser, error: authError } =
                        await adminAuthClient.auth.admin.getUserById(userId);
                      if (!authError && authUser?.user) {
                        const metadata = authUser.user.user_metadata || {};
                        const email = authUser.user.email || '';
                        const fullName = metadata.full_name || metadata.name || '';
                        const displayName =
                          fullName || email.split('@')[0] || `User ${userId.substring(0, 8)}...`;
                        userNamesMap.set(userId, displayName);
                      }
                    } catch (authErr) {
                      console.warn(`Could not fetch auth user ${userId} (fallback):`, authErr);
                    }
                  }
                }

                // Final fallback for any still missing
                missingUserIds.forEach(userId => {
                  if (!userNamesMap.has(userId)) {
                    userNamesMap.set(
                      userId,
                      userId.includes('@')
                        ? userId.split('@')[0]
                        : `User ${userId.substring(0, 8)}...`
                    );
                  }
                });
              } catch (fallbackErr) {
                console.warn('Error in fallback user name lookup (fallback):', fallbackErr);
                // Final fallback: use shortened user IDs
                missingUserIds.forEach(userId => {
                  if (!userNamesMap.has(userId)) {
                    userNamesMap.set(
                      userId,
                      userId.includes('@')
                        ? userId.split('@')[0]
                        : `User ${userId.substring(0, 8)}...`
                    );
                  }
                });
              }
            }

            // Final fallback: Use user ID for any still missing
            userIdArray.forEach(userId => {
              if (!userNamesMap.has(userId)) {
                userNamesMap.set(userId, `User ${userId.substring(0, 8)}...`);
              }
            });
          }

          // Map audit logs to zones
          const zoneAuditMap = new Map<
            string,
            {
              created?: { userId: string; timestamp: string };
              updated?: { userId: string; timestamp: string };
            }
          >();
          auditLogs?.forEach(log => {
            if (!zoneAuditMap.has(log.resource_id)) {
              zoneAuditMap.set(log.resource_id, {});
            }
            const zoneAudit = zoneAuditMap.get(log.resource_id)!;
            if (log.action === 'zone.created' && !zoneAudit.created) {
              zoneAudit.created = { userId: log.user_id, timestamp: log.created_at };
            } else if (log.action === 'zone.updated' && !zoneAudit.updated) {
              zoneAudit.updated = { userId: log.user_id, timestamp: log.created_at };
            }
          });

          // Enrich zones with user names
          zones = zones.map(zone => {
            const audit = zoneAuditMap.get(zone.id);
            return {
              ...zone,
              created_by: audit?.created?.userId || zone.created_by,
              updated_by: audit?.updated?.userId || zone.updated_by,
              created_by_name: audit?.created?.userId
                ? userNamesMap.get(audit.created.userId)
                : undefined,
              updated_by_name: audit?.updated?.userId
                ? userNamesMap.get(audit.updated.userId)
                : undefined
            };
          });
        }

        return {
          zones,
          totalCount: count || 0,
          hasMore: offset + limit < (count || 0)
        };
      }

      // Transform the result to match Zone interface
      let zones = (data || []).map((zone: any) => ({
        id: zone.id,
        zone_name: zone.zone_name,
        zone_slug: zone.zone_slug,
        description: zone.description,
        region: zone.region,
        country: zone.country,
        status: zone.status,
        metadata: zone.metadata,
        zone_size: zone.zone_size,
        total_recalls_at_creation: zone.total_recalls_at_creation,
        unique_vins_at_creation: zone.unique_vins_at_creation,
        total_recalls_current: zone.total_recalls_current,
        latest_snapshot_date: zone.latest_snapshot_date || undefined,
        created_at: zone.created_at,
        updated_at: zone.updated_at,
        created_by: zone.created_by,
        updated_by: zone.updated_by,
        oem: zone.oem,
        zip_codes: [], // Don't fetch actual zip codes for performance
        zip_code_count: Number(zone.zip_code_count) || 0
      }));

      // Fetch user names for created_by and updated_by from audit logs
      // NOTE: This is expensive and can slow down initial load. Only fetch if requested.
      if (includeUserNames && zones.length > 0) {
        const zoneIds = zones.map((z: Zone) => z.id);
        let auditLogs: any[] | null = null;

        // Only fetch audit logs if we have zones (avoid unnecessary query)
        // This can be made async/lazy in the future for better performance
        try {
          const { data: auditLogsData, error: auditError } = await prodClient
            .from('audit_logs')
            .select('resource_id, user_id, action, created_at')
            .eq('resource_type', 'zone')
            .in('resource_id', zoneIds)
            .in('action', ['zone.created', 'zone.updated'])
            .order('created_at', { ascending: false })
            .limit(100); // Limit to avoid slow queries

          if (auditError) {
            console.warn('Error fetching audit logs for zones (non-blocking):', auditError);
            // Continue without user names - don't block the response
          } else {
            auditLogs = auditLogsData;
          }
        } catch (auditErr) {
          console.warn('Error fetching audit logs for zones (non-blocking):', auditErr);
          // Continue without user names - don't block the response
        }

        // Get unique user IDs from audit logs
        const userIds = new Set<string>();
        auditLogs?.forEach(log => {
          if (log.user_id) userIds.add(log.user_id);
        });

        // Fetch user names from multiple sources
        const userNamesMap = new Map<string, string>();
        if (userIds.size > 0) {
          const userIdArray = Array.from(userIds);

          // Try to get from user_profiles first (prod schema)
          try {
            const { data: userProfiles, error: profileError } = await prodClient
              .from('user_profiles')
              .select('user_id, first_name, last_name')
              .in('user_id', userIdArray);

            if (!profileError && userProfiles) {
              userProfiles.forEach((user: any) => {
                const displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
                if (displayName) {
                  userNamesMap.set(user.user_id, displayName);
                }
              });
            }
          } catch (profileErr) {
            console.warn('Error fetching user profiles:', profileErr);
          }

          // Fallback: Try to get from auth.users using admin auth client
          const missingUserIds = userIdArray.filter(id => !userNamesMap.has(id));
          if (missingUserIds.length > 0) {
            try {
              // Create admin auth client for accessing auth.users
              const supabaseUrl =
                import.meta.env.VITE_SUPABASE_URL || 'https://exycbmlolhxpacnyqvmw.supabase.co';
              const serviceRoleKey =
                import.meta.env['VITE_SUPABASE_SERVICE_ROLE_KEY'] ||
                import.meta.env['SUPABASE_SERVICE_ROLE_KEY'];

              if (serviceRoleKey) {
                const adminAuthClient = createClient(supabaseUrl, serviceRoleKey, {
                  auth: {
                    autoRefreshToken: false,
                    persistSession: false
                  }
                });

                // Fetch user info from auth.users for missing users
                for (const userId of missingUserIds) {
                  try {
                    const { data: authUser, error: authError } =
                      await adminAuthClient.auth.admin.getUserById(userId);
                    if (!authError && authUser?.user) {
                      const metadata = authUser.user.user_metadata || {};
                      const email = authUser.user.email || '';
                      const fullName = metadata.full_name || metadata.name || '';
                      const displayName =
                        fullName || email.split('@')[0] || `User ${userId.substring(0, 8)}...`;
                      userNamesMap.set(userId, displayName);
                    }
                  } catch (authErr) {
                    console.warn(`Could not fetch auth user ${userId}:`, authErr);
                  }
                }
              }

              // Final fallback for any still missing
              missingUserIds.forEach(userId => {
                if (!userNamesMap.has(userId)) {
                  userNamesMap.set(
                    userId,
                    userId.includes('@')
                      ? userId.split('@')[0]
                      : `User ${userId.substring(0, 8)}...`
                  );
                }
              });
            } catch (fallbackErr) {
              console.warn('Error in fallback user name lookup:', fallbackErr);
              // Final fallback: use shortened user IDs
              missingUserIds.forEach(userId => {
                if (!userNamesMap.has(userId)) {
                  userNamesMap.set(
                    userId,
                    userId.includes('@')
                      ? userId.split('@')[0]
                      : `User ${userId.substring(0, 8)}...`
                  );
                }
              });
            }
          }

          // Final fallback: Use user ID for any still missing
          userIdArray.forEach(userId => {
            if (!userNamesMap.has(userId)) {
              userNamesMap.set(userId, `User ${userId.substring(0, 8)}...`);
            }
          });
        }

        // Map audit logs to zones
        const zoneAuditMap = new Map<
          string,
          {
            created?: { userId: string; timestamp: string };
            updated?: { userId: string; timestamp: string };
          }
        >();
        auditLogs?.forEach(log => {
          if (!zoneAuditMap.has(log.resource_id)) {
            zoneAuditMap.set(log.resource_id, {});
          }
          const zoneAudit = zoneAuditMap.get(log.resource_id)!;
          if (log.action === 'zone.created' && !zoneAudit.created) {
            zoneAudit.created = { userId: log.user_id, timestamp: log.created_at };
          } else if (log.action === 'zone.updated' && !zoneAudit.updated) {
            zoneAudit.updated = { userId: log.user_id, timestamp: log.created_at };
          }
        });

        // Enrich zones with user names
        zones = zones.map(zone => {
          const audit = zoneAuditMap.get(zone.id);
          return {
            ...zone,
            created_by: audit?.created?.userId || zone.created_by,
            updated_by: audit?.updated?.userId || zone.updated_by,
            created_by_name: audit?.created?.userId
              ? userNamesMap.get(audit.created.userId)
              : undefined,
            updated_by_name: audit?.updated?.userId
              ? userNamesMap.get(audit.updated.userId)
              : undefined
          };
        });
      }

      // Apply region filter client-side (RPC doesn't support region filter)
      if (regionFilter && regionFilter !== 'all') {
        zones = zones.filter(z => z.region === regionFilter);
      }

      // Extract total count from first row (all rows have the same total_count)
      // Note: When region filter is applied client-side, count may be inaccurate
      const totalCount =
        regionFilter && regionFilter !== 'all' ? zones.length : data?.[0]?.total_count || 0;

      return {
        zones,
        totalCount: Number(totalCount),
        hasMore:
          regionFilter && regionFilter !== 'all'
            ? false // Can't paginate accurately with client-side filter
            : offset + limit < totalCount
      };
    } catch (error) {
      console.error('Error fetching paginated zones:', error);
      throw error;
    }
  },

  /**
   * Get zip codes for a zone
   * Uses prod schema client - zone_zip_codes has RLS policies
   */
  async getZoneZipCodes(zoneId: string): Promise<string[]> {
    try {
      // Use main supabase client - public schema wrapper delegates to prod function
      // This uses SECURITY DEFINER RPC function to bypass RLS policies
      // eslint-disable-next-line no-console
      console.log(`[getZoneZipCodes] Fetching zip codes for zone ID: ${zoneId}`);

      const { data, error } = await supabase.rpc('get_zone_zip_codes_for_admin', {
        p_zone_id: zoneId
      });

      if (error) {
        // Log full Supabase error details for debugging
        // eslint-disable-next-line no-console
        console.error('[getZoneZipCodes] RPC error:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
          zoneId
        });

        // Check if function doesn't exist (migration not run yet)
        if (error.code === 'PGRST202' || error.message?.includes('Could not find the function')) {
          throw new Error(
            'RPC function not found. Please run the migration: migrations/add-get-zone-zip-codes-rpc-function.sql\n' +
              `Error: ${error.message}`
          );
        }

        // Check if it's an access denied error
        if (
          error.code === '42501' ||
          error.message?.includes('Access denied') ||
          error.message?.includes('Super admin')
        ) {
          // Access denied - throw error so React Query can handle it
          throw new Error(
            `Access denied: ${error.message || 'Super admin role required to read zone zip codes.'}`
          );
        }

        // For other errors, throw to let React Query handle retries
        throw new Error(`Failed to fetch zone zip codes: ${error.message}`);
      }

      // RPC returns array of objects with zip_code property
      const zipCodes = (data || []).map((row: any) => row.zip_code);
      // eslint-disable-next-line no-console
      console.log(
        `[getZoneZipCodes] Successfully fetched ${zipCodes.length} zip codes for zone ${zoneId}`
      );

      return zipCodes;
    } catch (error) {
      // Log unexpected errors
      // eslint-disable-next-line no-console
      console.error('[getZoneZipCodes] Error fetching zone zip codes:', {
        error,
        zoneId,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined
      });

      // Re-throw error so React Query can handle it properly
      throw error;
    }
  },

  /**
   * Set zip codes for a zone using optimized RPC function
   * This uses a server-side function that performs differential updates in a single transaction
   */
  async setZoneZipCodes(
    zoneId: string,
    zipCodes: string[]
  ): Promise<{ inserted: number; deleted: number; total: number }> {
    try {
      // Use prod client for RPC calls to prod schema functions
      const prodClient = getProdSupabaseClient();

      // Call the optimized RPC function that handles differential updates atomically
      const { data, error } = await prodClient.rpc('update_zone_zip_codes', {
        p_zone_id: zoneId,
        p_zip_codes: zipCodes.map(z => z.trim()).filter(z => z.length > 0)
      });

      if (error) {
        console.error('Error updating zone ZIP codes:', error);
        throw error;
      }

      // Return the operation counts
      const result = data?.[0] || { inserted_count: 0, deleted_count: 0, total_count: 0 };
      return {
        inserted: result.inserted_count,
        deleted: result.deleted_count,
        total: result.total_count
      };
    } catch (error) {
      console.error('Failed to update zone ZIP codes:', error);
      throw error;
    }
  },

  /**
   * Get available zip codes from the database
   * For now, returns empty array - users can manually add zip codes
   * TODO: When cars_with_safety_recalls_mv is available, can populate from there
   */
  async getAvailableZipCodes(): Promise<ZipCode[]> {
    // Return empty array for now - users will manually add zip codes
    // This can be enhanced later when cars_with_safety_recalls_mv is available
    return [];
  },

  /**
   * Validate that ZIP codes don't overlap with existing zones using optimized RPC function
   * @param zipCodes - Array of ZIP codes to validate
   * @param excludeZoneId - Zone ID to exclude from overlap check (for edits)
   * @param oem - OEM to check overlaps within (overlaps only checked within same OEM)
   */
  async validateZipCodeOverlaps(
    zipCodes: string[],
    excludeZoneId?: string,
    oem: string = 'Ford' // NEW: OEM parameter - overlaps only checked within same OEM
  ): Promise<{
    hasOverlaps: boolean;
    conflicts: Array<{ zoneName: string; overlappingZips: string[] }>;
  }> {
    if (!zipCodes || zipCodes.length === 0) {
      return { hasOverlaps: false, conflicts: [] };
    }

    try {
      // Use prod client for RPC calls to prod schema functions
      const prodClient = getProdSupabaseClient();

      // Use the optimized RPC function for validation
      const { data, error } = await prodClient.rpc('validate_zip_code_overlaps', {
        p_zip_codes: zipCodes.map(z => z.trim()).filter(z => z.length > 0),
        p_exclude_zone_id: excludeZoneId || null,
        p_oem: oem // NEW: Pass OEM to check overlaps only within same OEM
      });

      if (error) {
        console.error('Error checking ZIP code overlaps:', error);
        throw error;
      }

      const result = data?.[0];
      if (!result || !result.has_overlaps) {
        return { hasOverlaps: false, conflicts: [] };
      }

      // Transform the JSONB result into our expected format
      const conflictingZones = result.conflicting_zones || [];
      const conflicts = conflictingZones.map((conflict: any) => ({
        zoneName: conflict.zone_name,
        overlappingZips: conflict.overlapping_zips || []
      }));

      return {
        hasOverlaps: true,
        conflicts
      };
    } catch (error) {
      console.error('Error validating ZIP code overlaps:', error);
      throw error;
    }
  },

  /**
   * Create a new zone
   * Uses SECURITY DEFINER RPC function - works with regular authenticated client
   */
  async createZone(data: CreateZoneData, userId?: string): Promise<Zone> {
    try {
      // Use prod client with SECURITY DEFINER RPC function
      const prodClient = getProdSupabaseClient();

      // Validate ZIP code overlaps before creating zone (within same OEM)
      if (data.zip_codes && data.zip_codes.length > 0) {
        const validation = await this.validateZipCodeOverlaps(data.zip_codes, undefined, data.oem);
        if (validation.hasOverlaps) {
          const conflictDetails = validation.conflicts
            .map(conflict => `${conflict.zoneName}: ${conflict.overlappingZips.join(', ')}`)
            .join('; ');
          throw new Error(
            `ZIP code overlap detected. The following ZIP codes already exist in other ${data.oem} zones: ${conflictDetails}`
          );
        }
      }

      // Generate zone_slug if not provided
      const zoneSlug = data.zone_slug || data.zone_name.toLowerCase().replace(/[^a-z0-9]+/g, '-');

      // Call the RPC function with OEM
      const { data: result, error } = await prodClient.rpc('create_zone_for_admin', {
        p_zone_name: data.zone_name,
        p_zone_slug: zoneSlug,
        p_description: data.description || null,
        p_region: data.region || null,
        p_country: data.country || 'US',
        p_status: data.status || 'active',
        p_zone_size: data.zone_size || 'small',
        p_metadata: data.metadata || {},
        p_total_recalls_at_creation: data.total_recalls_at_creation || 0,
        p_unique_vins_at_creation: data.unique_vins_at_creation || 0,
        p_oem: data.oem // NEW: Pass OEM to RPC
      });

      if (error) {
        throw error;
      }

      // RPC returns an array, get the first item
      const newZone = Array.isArray(result) ? result[0] : result;

      if (!newZone) {
        throw new Error('Failed to create zone - no data returned');
      }

      // Set zip codes if provided
      let zipCodes: string[] = [];
      let zipCodeCount = 0;
      const totalRecallsAtCreation = data.total_recalls_at_creation ?? 0;
      if (data.zip_codes && data.zip_codes.length > 0) {
        const zipResult = await this.setZoneZipCodes(newZone.id, data.zip_codes);
        zipCodes = data.zip_codes;
        zipCodeCount = zipResult.total;

        // Backfill the creation baseline ONLY when the caller didn't supply one — the map
        // tab computes it from the same unscoped RPC before saving, the list tab doesn't.
        // Previously this ran unconditionally and overwrote the caller's value with a
        // differently-scoped one, which is how zone_size, metadata.recallCount and
        // total_recalls_at_creation ended up disagreeing on the same row.
        if (data.total_recalls_at_creation == null) {
          // Deliberately not awaited: the RPC can be slow for large zones and zone
          // creation should return immediately.
          (async () => {
            try {
              const recallCounts = await this.getCurrentRecallCountsForZones([newZone.id]);
              const count = recallCounts[newZone.id] ?? 0;
              await prodClient.rpc('update_zone_for_admin', {
                p_zone_id: newZone.id,
                p_total_recalls_at_creation: count
              });
            } catch (recallError) {
              console.warn('[createZone] Background recall count failed:', recallError);
            }
          })();
        }
      }

      const zoneResult = {
        ...newZone,
        zip_codes: zipCodes,
        zip_code_count: zipCodeCount,
        total_recalls_at_creation: totalRecallsAtCreation,
        // Seed the optimistic cache entry so the size badge doesn't flash "Small" before
        // the refetch lands.
        total_recalls_current: totalRecallsAtCreation
      };

      // Log audit event
      if (userId) {
        await logZoneAudit({
          userId,
          action: 'zone.created',
          resourceId: newZone.id,
          newValues: {
            zone_name: newZone.zone_name,
            zone_slug: newZone.zone_slug,
            region: newZone.region,
            country: newZone.country,
            status: newZone.status,
            zip_codes: zipCodes,
            zip_code_count: zipCodes.length,
            metadata: newZone.metadata,
            created_via_ui: true
          }
        });
      }

      return zoneResult;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get current recall counts for multiple zones in a single batch query.
   * Uses ZIP code mapping + latest snapshot per zone_slug to count unique (VIN, campaign_number) pairs.
   *
   * @param zoneIds - Array of zone UUIDs to get counts for
   * @param tenantId - Optional tenant ID to filter recalls (matches map behavior).
   *                   When provided, only counts recalls belonging to the specified tenant.
   *                   When omitted/null, counts all recalls in the zones (legacy behavior).
   */
  async getCurrentRecallCountsForZones(
    zoneIds: string[],
    tenantId?: string
  ): Promise<Record<string, number>> {
    if (!zoneIds || zoneIds.length === 0) return {};

    const prodClient = getProdSupabaseClient();
    const { data, error } = await prodClient.rpc('get_current_recall_counts_for_zones', {
      p_zone_ids: zoneIds,
      p_tenant_id: tenantId || null
    });

    if (error) {
      console.error('[tenantAPI] Error fetching current recall counts:', error);
      throw error;
    }

    // Convert array of {zone_id, total_recalls_current} to a map
    const result: Record<string, number> = {};
    if (data && Array.isArray(data)) {
      data.forEach((row: { zone_id: string; total_recalls_current: number }) => {
        result[row.zone_id] = row.total_recalls_current || 0;
      });
    }
    return result;
  },

  /**
   * Refresh denormalized current recall counts for all active zones.
   * Uses SECURITY DEFINER RPC function in prod schema.
   */
  async refreshZoneRecallCounts(): Promise<void> {
    const prodClient = getProdSupabaseClient();
    const { error } = await prodClient.rpc('refresh_zone_recall_counts');

    if (error) {
      console.error('[tenantAPI] Error refreshing zone recall counts:', error);
      throw error;
    }
  },

  /**
   * Update an existing zone
   * Uses SECURITY DEFINER RPC function - works with regular authenticated client
   */
  async updateZone(zoneId: string, data: UpdateZoneData, userId?: string): Promise<Zone> {
    try {
      // Use prod client with SECURITY DEFINER RPC function
      const prodClient = getProdSupabaseClient();

      // Only fetch old values if zip_codes are being changed (for validation) or if audit logging is needed
      const zipCodesChanged = data.zip_codes !== undefined;
      const needsAuditLogging = userId !== undefined;

      // We need to fetch the existing zone's OEM for ZIP code validation
      let existingOem: string = 'Ford'; // Default fallback
      let oldValues: Record<string, any> | undefined;

      // Fetch old values if: audit logging needed, OR zip codes are changing (need OEM for validation)
      if (needsAuditLogging || zipCodesChanged) {
        // Only fetch old values if zip codes are changing (for audit comparison)
        // Use supabase (public schema) to call the public wrapper
        try {
          const { data: oldZoneData, error: oldZoneError } = await supabase.rpc(
            'get_zone_by_id_for_admin',
            { p_zone_id: zoneId }
          );

          if (!oldZoneError && oldZoneData && oldZoneData.length > 0) {
            const oldZone = oldZoneData[0];
            existingOem = oldZone.oem || 'Ford'; // Capture existing OEM for ZIP validation
            const oldZipCodes = await this.getZoneZipCodes(zoneId);
            oldValues = {
              zone_name: oldZone.zone_name,
              zone_slug: oldZone.zone_slug,
              description: oldZone.description,
              region: oldZone.region,
              country: oldZone.country,
              status: oldZone.status,
              oem: oldZone.oem,
              zip_codes: oldZipCodes,
              zip_code_count: oldZipCodes.length,
              metadata: oldZone.metadata
            };
          }
        } catch (auditError) {
          console.warn('Failed to get old zone values for audit:', auditError);
        }
      }

      // Call the RPC function with only the fields that are being updated
      const { data: result, error } = await prodClient.rpc('update_zone_for_admin', {
        p_zone_id: zoneId,
        p_zone_name: data.zone_name ?? null,
        p_zone_slug: data.zone_slug ?? null,
        p_description: data.description ?? null,
        p_region: data.region ?? null,
        p_country: data.country ?? null,
        p_status: data.status ?? null,
        p_zone_size: data.zone_size ?? null,
        p_metadata: data.metadata ?? null,
        p_total_recalls_at_creation: data.total_recalls_at_creation ?? null,
        p_oem: data.oem ?? null
      });

      if (error) {
        throw error;
      }

      // RPC returns an array, get the first item
      const updatedZone = Array.isArray(result) ? result[0] : result;

      if (!updatedZone) {
        throw new Error('Failed to update zone - no data returned');
      }

      // Only update zip codes if they were explicitly provided and changed
      let zipCodes: string[] = [];
      let zipCodeCount: number;
      if (zipCodesChanged) {
        // Use the OEM from update data if provided, otherwise use existing zone's OEM
        const oemForValidation = data.oem || existingOem;

        // Validate ZIP code overlaps before updating zone (exclude current zone, within same OEM)
        const validation = await this.validateZipCodeOverlaps(
          data.zip_codes!,
          zoneId,
          oemForValidation
        );
        if (validation.hasOverlaps) {
          const conflictDetails = validation.conflicts
            .map(conflict => `${conflict.zoneName}: ${conflict.overlappingZips.join(', ')}`)
            .join('; ');
          throw new Error(
            `ZIP code overlap detected. The following ZIP codes already exist in other ${oemForValidation} zones: ${conflictDetails}`
          );
        }

        const zipResult = await this.setZoneZipCodes(zoneId, data.zip_codes!);
        zipCodes = data.zip_codes!;
        // Use the actual count from the database to ensure accuracy
        zipCodeCount = zipResult.total;
      } else {
        // If zip codes weren't changed, only fetch them if needed for the return value
        // For simple name updates, we can skip this expensive query
        zipCodes = await this.getZoneZipCodes(zoneId);
        zipCodeCount = zipCodes.length;
      }

      const zoneResult = {
        ...updatedZone,
        zip_codes: zipCodes,
        zip_code_count: zipCodeCount
      };

      // Log audit event (only if we have old values from zip code changes)
      if (userId && oldValues) {
        await logZoneAudit({
          userId,
          action: 'zone.updated',
          resourceId: zoneId,
          oldValues,
          newValues: {
            zone_name: updatedZone.zone_name,
            zone_slug: updatedZone.zone_slug,
            description: updatedZone.description,
            region: updatedZone.region,
            country: updatedZone.country,
            status: updatedZone.status,
            zip_codes: zipCodes,
            zip_code_count: zipCodes.length,
            metadata: updatedZone.metadata,
            updated_via_ui: true
          }
        });
      }

      return zoneResult;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete a zone
   * Uses SECURITY DEFINER RPC function - works with regular authenticated client
   */
  async deleteZone(zoneId: string, hardDelete: boolean = false, userId?: string): Promise<void> {
    try {
      // Use prod client with SECURITY DEFINER RPC function
      const prodClient = getProdSupabaseClient();

      // Get zone values for audit logging before deletion using RPC
      let oldValues: Record<string, any> | undefined;
      if (userId) {
        try {
          // Use supabase (public schema) to call the public wrapper
          const { data: oldZoneData, error: oldZoneError } = await supabase.rpc(
            'get_zone_by_id_for_admin',
            { p_zone_id: zoneId }
          );

          if (!oldZoneError && oldZoneData && oldZoneData.length > 0) {
            const oldZone = oldZoneData[0];
            const oldZipCodes = await this.getZoneZipCodes(zoneId);
            oldValues = {
              zone_name: oldZone.zone_name,
              zone_slug: oldZone.zone_slug,
              description: oldZone.description,
              region: oldZone.region,
              country: oldZone.country,
              status: oldZone.status,
              zip_codes: oldZipCodes,
              zip_code_count: oldZipCodes.length,
              metadata: oldZone.metadata
            };
          }
        } catch (auditError) {
          console.warn('Failed to get zone values for delete audit:', auditError);
        }
      }

      if (hardDelete) {
        // Hard delete using RPC function
        const { error } = await prodClient.rpc('delete_zone_for_admin', {
          p_zone_id: zoneId
        });

        if (error) {
          throw error;
        }
      } else {
        // Soft delete - set status to inactive using update RPC
        await this.updateZone(zoneId, { status: 'inactive' }, userId);
      }

      // Log audit event
      if (userId && oldValues) {
        await logZoneAudit({
          userId,
          action: hardDelete ? 'zone.deleted' : 'zone.soft_deleted',
          resourceId: zoneId,
          oldValues,
          newValues: {
            deleted_via_ui: true,
            delete_type: hardDelete ? 'hard_delete' : 'soft_delete',
            deletion_timestamp: new Date().toISOString()
          }
        });
      }
    } catch (error) {
      throw error;
    }
  }
};
